import { type MouseEvent, type ReactNode } from 'react';
import { Pill } from '../components/Pill/Pill';
import { renderOverflowTooltipLabel } from './components';
import { getLabelText } from './plain-label.utils';
import { type Maybe } from './maybe';

function findOptionByValue(
    options: Maybe<unknown[]>,
    optionValue: Maybe<string>,
    val: unknown,
): unknown {
    if (!optionValue) {
        return val;
    }

    if (!Array.isArray(options)) {
        return undefined;
    }

    return options.find(
        (option) =>
            Boolean(option) &&
            typeof option === 'object' &&
            (option as Record<string, unknown>)[optionValue] === val,
    );
}

export interface SelectedPillItem {
    value: unknown;
    label: Maybe<string>;
}

export function resolveSelectedPillItems(
    value: Maybe<unknown[]>,
    options: Maybe<unknown[]>,
    optionLabel?: string,
    optionValue?: string,
): SelectedPillItem[] {
    if (!Array.isArray(value)) {
        return [];
    }

    return value.map((val) => {
        const matchedOption = findOptionByValue(options, optionValue, val);

        return {
            value: val,
            label: getLabelText(matchedOption ?? val, optionLabel),
        };
    });
}

export interface CreateDefaultChipTemplateInput {
    options: Maybe<unknown[]>;
    optionLabel?: string;
    optionValue?: string;
    onRemoveValue: (
        value: unknown,
        event: MouseEvent<HTMLButtonElement>,
    ) => void;
}

export function createDefaultChipTemplate({
    options,
    optionLabel,
    optionValue,
    onRemoveValue,
}: CreateDefaultChipTemplateInput): (val: unknown) => ReactNode {
    return function MultiSelectChipTemplate(val: unknown): ReactNode {
        if (val === null || typeof val === 'undefined') {
            return null;
        }

        const matchedOption = findOptionByValue(options, optionValue, val);
        const label = getLabelText(matchedOption ?? val, optionLabel);

        return (
            <Pill
                onDismiss={(event) => {
                    onRemoveValue(val, event);
                }}
                dismissAriaLabel={label ? `Remove ${label}` : 'Remove'}
            >
                {renderOverflowTooltipLabel(label, 'ics-overflow-label')}
            </Pill>
        );
    };
}
